import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BarChart3, Users, Send, DollarSign, Package, TrendingUp, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KPI = ({ icon: Icon, label, value, color, subtext }) => (
  <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-5">
    <div className="flex items-center justify-between mb-3">
      <Icon size={20} strokeWidth={1.5} style={{ color }} />
      <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{value}</span>
    </div>
    <p className="text-sm text-[#B0B0B0]">{label}</p>
    {subtext && <p className="text-xs text-[#808080] mt-0.5">{subtext}</p>}
  </div>
);

export default function ReportsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reports/overview`, { withCredentials: true });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-[#1A1A1A] rounded w-48" />
          <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-24 bg-[#1A1A1A] rounded-lg" />)}</div>
        </div>
      </div>
    );
  }

  const g = data?.global || {};
  const businesses = data?.businesses || [];

  return (
    <div className="flex-1 p-8" data-testid="reports-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>
          <BarChart3 size={28} className="inline mr-2 text-[#00FF99]" />
          Reports
        </h1>
        <p className="text-[#B0B0B0] text-sm mt-1">Business performance overview (Super Admin)</p>
      </div>

      {/* Global KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <KPI icon={Users} label="Total Contacts" value={g.total_contacts || 0} color="#00FF99" />
        <KPI icon={Send} label="Campaigns Sent" value={g.total_sent || 0} color="#007AFF" />
        <KPI icon={Package} label="Products Sold" value={g.total_products || 0} color="#FFB800" />
        <KPI icon={DollarSign} label="Total Revenue" value={`$${(g.total_value || 0).toLocaleString()}`} color="#00FF99" />
        <KPI icon={TrendingUp} label="Avg Value" value={g.total_contacts > 0 ? `$${(g.total_value / g.total_contacts).toFixed(0)}` : '$0'} color="#9B59B6" subtext="per contact" />
      </div>

      {/* Per-Business Breakdown */}
      <h2 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>Business Breakdown</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        {businesses.map(b => (
          <div key={b.business_id} className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ backgroundColor: `${b.color}20` }}>
                <Building2 size={20} style={{ color: b.color }} />
              </div>
              <div>
                <h3 className="text-white font-bold">{b.business_name}</h3>
                <p className="text-xs text-[#808080]">{b.business_type}</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-[#808080]">Contacts</span>
                <span className="text-sm text-white font-medium">{b.contact_count}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-[#808080]">Leads</span>
                <span className="text-sm text-white font-medium">{b.lead_count}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-[#808080]">Products Sold</span>
                <span className="text-sm text-white font-medium">{b.total_products}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-[#808080]">Revenue</span>
                <span className="text-sm font-bold" style={{ color: b.color }}>${b.total_value.toLocaleString()}</span>
              </div>
              <div className="border-t border-[rgba(255,255,255,0.06)] pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[#808080]">Campaigns</span>
                  <span className="text-sm text-white">{b.campaign_count} ({b.sent_campaigns} sent)</span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-xs text-[#808080]">Mailing Lists</span>
                  <span className="text-sm text-white">{b.list_count}</span>
                </div>
              </div>
            </div>

            {/* Revenue bar */}
            {g.total_value > 0 && b.total_value > 0 && (
              <div className="mt-3">
                <div className="h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(b.total_value / g.total_value * 100).toFixed(0)}%`, backgroundColor: b.color }} />
                </div>
                <p className="text-xs text-[#808080] mt-1">{(b.total_value / g.total_value * 100).toFixed(1)}% of total revenue</p>
              </div>
            )}
          </div>
        ))}
        {businesses.length === 0 && (
          <div className="col-span-full text-center py-8 text-[#808080]">
            <p>No businesses to report on yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}
